import { useMemo } from "react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from "@/components/ui/pagination";

type DataPaginationProps = {
  page: number;
  pageCount: number;
  maxPageCount?: number;
  maxItems?: number;
  onChange: (page: number) => void;
};

const pageNumberLinkClass = (isActive: boolean) =>
  isActive
    ? "w-fit border-border px-3 text-foreground"
    : "w-fit border-transparent px-3 text-foreground shadow-none";

export const DataPagination: React.FC<DataPaginationProps> = ({
  page,
  pageCount,
  maxPageCount,
  maxItems = 10,
  onChange
}) => {
  const items = useMemo(() => {
    const min = Math.max(1, page - maxItems / 2);
    const max = Math.min(pageCount, min + (maxItems - 1));
    const result: number[] = [];

    for (let i = min; i <= max; i++) {
      result.push(i);
    }

    return result;
  }, [page, pageCount, maxItems]);

  if (pageCount === 0) {
    return (
      <div className="mt-6 flex flex-col gap-4">
        <Pagination>
          <PaginationContent>
            {page > 1 && (
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => {
                    onChange(page - 1);
                  }}
                />
              </PaginationItem>
            )}
            {(typeof maxPageCount !== "number" || page < maxPageCount) && (
              <PaginationItem>
                <PaginationNext
                  onClick={() => {
                    onChange(page + 1);
                  }}
                />
              </PaginationItem>
            )}
          </PaginationContent>
        </Pagination>
      </div>
    );
  }

  if (items.length <= 1) {
    return null;
  }

  return (
    <div className="mt-6 flex flex-col gap-4">
      <Pagination>
        <PaginationContent>
          {page > 1 && (
            <PaginationItem>
              <PaginationPrevious
                onClick={() => {
                  onChange(page - 1);
                }}
              />
            </PaginationItem>
          )}
          {items[0] > 2 && (
            <PaginationItem>
              <PaginationLink
                onClick={() => {
                  onChange(1); // First page
                }}
                className={pageNumberLinkClass(page === 1)}
              >
                1
              </PaginationLink>
            </PaginationItem>
          )}
          {items[0] > 1 && (
            <PaginationItem>
              <PaginationEllipsis className="border-gray-dark-200" />
            </PaginationItem>
          )}
          {items.map((index) => {
            return (
              <PaginationItem key={index}>
                <PaginationLink
                  key={index}
                  isActive={page === index}
                  onClick={() => {
                    onChange(index);
                  }}
                  className={pageNumberLinkClass(page === index)}
                >
                  {index}
                </PaginationLink>
              </PaginationItem>
            );
          })}
          {items[items.length - 1] < pageCount && (
            <>
              <PaginationItem>
                <PaginationEllipsis className="border-gray-dark-200" />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  onClick={() => {
                    onChange(pageCount);
                  }}
                  className={pageNumberLinkClass(page === pageCount)}
                >
                  {pageCount}
                </PaginationLink>
              </PaginationItem>
            </>
          )}
          {page < pageCount && (
            <PaginationItem>
              <PaginationNext
                onClick={() => {
                  onChange(page + 1);
                }}
              />
            </PaginationItem>
          )}
        </PaginationContent>
      </Pagination>
      {/* <span className="subtext-small flex flex-row justify-center text-gray-dark-1000">{`Page ${page} of ${pageCount}`}</span> */}
    </div>
  );
};
